/**
 * Centralized UI Copy / Strings
 * All user-facing text for Our Love ♥ Connection
 * 
 * Structure: Organized by feature/page for maintainability
 * Usage: import { copy } from '@/copy' then copy.section.key
 * 
 * Note: User-generated content (reflections, messages) is NOT included here.
 * This file is for static UI text only.
 */

export const copy = {
  // Onboarding
  onboarding: {
    screen1: {
      line1: "This is not swipe based dating.",
      line2: "This is a guided system for adults who want clarity before commitment, not confusion after.",
    },
    screen2: {
      line1: "If you've ever felt that modern dating moves too fast…",
      line2: "or that the most important conversations happen too late…",
      line3: "You're not alone.",
    },
    screen3: {
      line1: "Clarity changes everything.",
      line2: "If you're tired of investing in connections that were never aligned, you're in the right place.",
    },
    screen4: {
      line1: "Here, connection begins with self awareness.",
      line2: "Start by completing your Relationship Map.",
      cta: "Begin Your Journey",
    },
  },

  // Brand
  brand: {
    name: "Our Love ♥ Connection",
    tagline: "Because building a real future matters.",
    copyright: (year) => `© ${year} Our Love Connection`,
  },

  // Navigation
  nav: {
    home: "Home",
    myReflections: "My Reflections",
    about: "About",
    signIn: "Sign In",
    signOut: "Sign Out",
    backToHome: "Back to home",
    backToJourney: "Back to Journey",
  },

  // Common / Shared
  common: {
    loading: "Loading...",
    error: "Something went wrong",
    continue: "Continue",
    review: "Review",
    save: "Save",
    cancel: "Cancel",
  },

  // Landing / Home Page
  landing: {
    headline: "Our Love ♥ Connection",
    introduction: "A guided system for adults who want clarity before commitment, not confusion after.",
    primaryCta: "Begin Your Journey",
    secondaryCta: "Sign In",
  },

  // Auth - Login
  login: {
    title: "Sign In",
    subtitle: "Enter your credentials to continue",
    emailLabel: "Email",
    emailPlaceholder: "your@email.com",
    passwordLabel: "Password",
    passwordPlaceholder: "••••••••",
    submitButton: "Sign In",
    submitting: "Signing in...",
    noAccount: "Don't have an account?",
    createOne: "Create one",
    welcomeBack: "Welcome Back",
    welcomeBackDescription: "A guided system for adults who want clarity before commitment, not confusion after.",
    successToast: "Welcome back!",
    errorToast: "Failed to sign in. Please check your credentials.",
  },

  // Auth - Register
  register: {
    title: "Create Account",
    subtitle: "For adults serious about building a real future.",
    nameLabel: "Your Name",
    namePlaceholder: "Your name",
    emailLabel: "Email",
    emailPlaceholder: "your@email.com",
    passwordLabel: "Password",
    passwordPlaceholder: "At least 8 characters",
    confirmPasswordLabel: "Confirm Password",
    confirmPasswordPlaceholder: "Confirm your password",
    submitButton: "Create Account",
    submitting: "Creating account...",
    hasAccount: "Already have an account?",
    signIn: "Sign in",
    heroTitle: "Begin Your Journey",
    heroDescription: "A guided system for adults who want clarity before commitment, not confusion after.",
    successToast: "Welcome to Our Love Connection!",
    errorToast: "Failed to create account. Please try again.",
    disclaimer: "Our Love Connection is a self-guided reflection platform. It is not therapy, counseling, or a mental health service. By creating an account, you agree to our Terms of Service and Privacy Policy.",
    passwordHelper: "Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter, one number, and one special character.",
  },

  // Map Intro — "How to Use This Map"
  mapIntro: {
    brandLabel: "OUR LOVE ♥ CONNECTION",
    title: "How to Use This Map",
    subtitle: "Before you begin, read this.",
    line1: "This is a private self-reflection tool.",
    line2: "It is not therapy. It is not a test. No one is grading you.",
    line3: "The Relationship Map is designed to help you understand yourself before you ask someone else to understand you.",
    line4: "Work through each checkpoint honestly – especially the ones that feel uncomfortable. Discomfort is often clarity in disguise.",
    line5: "There are no right answers. Only honest ones.",
    cta: "I'm ready to begin",
  },

  mapDisclaimer: {
    noteTitle: "A note before you begin:",
    line1: "This Relationship Map is a self-guided reflection tool designed to support personal clarity around values, readiness, and alignment.",
    line2: "It is not therapy, counseling, coaching, or mental health treatment. It does not provide diagnoses or professional advice.",
    line3: "Take your time. Answer honestly. This space is for you.",
    cta: "Begin Your Journey",
  },

  // Dashboard
  dashboard: {
    welcomeBack: (name) => `Welcome back, ${name}`,
    welcomeDescription: "Continue your journey toward building a real future.",
    orientationNote: "Profiles and photos are introduced after the Relationship Map is completed, so connections begin with clarity rather than browsing.",
    journeyLabel: "Your Journey",
    journeyTitle: "The Relationship Map",
    progressLabel: "Progress",
    progressCount: (completed, total) => `${completed} of ${total} checkpoints`,
    checkpointsTitle: "Your Checkpoints",
    journeyComplete: "Journey Complete!",
    journeyCompleteDescription: "View your full reflection summary",
    viewSummary: "View Summary",
    lockedToast: "Complete previous checkpoints to unlock this one",
    loadingJourney: "Loading your journey...",
    loadError: "Failed to load your journey",
    signOutSuccess: "Signed out successfully",
    calmReminder: "Take your time. There are no right or wrong answers.",
    leftLoop: "Know Yourself",
    rightLoop: "Build Together",
    alignmentLabel: "Alignment Checkpoint",
    alignmentPause: "Pause & Reflect",
    alignmentPrompt: "Am I being honest — or answering who I think I should be?",
    alignmentCta: "I'm ready — Continue",
    alignmentAcknowledged: "Acknowledged",
    alignmentLocked: "Complete Part 1 to reach this checkpoint.",
    alignmentCenter: "Pause. Reflect. Decide.",
    journeySubtitle: "Know yourself. Align together. Build forward.",
    checkpointsSubtitle: "Know yourself. Align together. Build forward.",
    beginHere: "Begin Here",
    mapPrivacyNote: "This map is your private framework. No one else needs to see it.",
    completionLine1: "You've done something most people never do.",
    completionLine2: "You stopped. You reflected. You got honest with yourself.",
    completionLine3: "Your Relationship Map is complete.",
    completionLine4: "You'll be among the first to know when introductions open. Until then, revisit your reflections anytime — and trust that clarity always leads somewhere real.",
    completionLine5: "Thank you for being here from the beginning.",
    completionLine6: "Because building a real future matters.",
    checkpointHints: {
      'foundation': 'Pause & Reflect',
      'faith-emotions': 'Be Honest Here',
      'stability': 'This Requires Clarity',
      'destination': 'Your Personal Vision',
    },
  },

  // Checkpoint / Reflection Flow
  checkpoint: {
    checkpointOf: (current, total) => `Checkpoint ${current} of ${total}`,
    requiredHelper: "This reflection is required to continue.",
    optionalLabel: "Optional — just for fun",
    textareaPlaceholder: "Take your time. There are no right or wrong answers...",
    optionalPlaceholder: "Share if you'd like...",
    saveAndPause: "Save & Pause",
    saveAndContinue: "Save & Continue",
    completeJourney: "Complete Journey",
    saving: "Saving...",
    savedToast: "Reflection saved",
    saveError: "Failed to save reflection. Please try again.",
    requiredError: "Please complete the reflection above to continue.",
    lockedError: "This checkpoint is locked. Complete previous checkpoints first.",
    loadError: "Failed to load checkpoint",
    progressSaved: "Your progress is saved automatically. Take your time.",
    confirmMessage: "Once submitted, your answers for this checkpoint cannot be changed. Take your time and answer honestly — this reflection is for you.",
    confirmPrompt: "Are you ready to continue?",
    confirmYes: "Yes, continue",
    confirmGoBack: "Go back",
    readOnlyNotice: "Your Relationship Map is complete. Answers can no longer be edited.",
    loadingCheckpoint: "Loading checkpoint...",
  },

  // My Reflections Page
  reflections: {
    title: "My Reflections",
    subtitle: "Your completed reflections, kept private for you.",
    emptyState: "You haven't completed any reflections yet.",
    startJourney: "Start Your Journey",
    privacyNote: "Your reflections are private and visible only to you.",
    loadingReflections: "Loading reflections...",
    loadError: "Failed to load reflections",
    notAnswered: "Not yet answered — you can revisit this anytime.",
    guidanceSection: "Guidance",
    guidanceDescription: "Available after completing The Relationship Map.",
  },

  // Red Flags & Compatibility Guide
  guide: {
    title: "Red Flags & Compatibility — A Quiet Guide",
    seriesTitle: "A Mini-Series on Red Flags & Compatibility",
    howToUse: {
      title: "How to use this mini-series",
      p1: "This is not a checklist.",
      p2: "It's not a diagnosis.",
      p3: "It's not meant to make you suspicious or guarded.",
      p4: "Think of this as quiet context — something to return to as you date, reflect, and decide what alignment actually looks like for you.",
      p5: "Read it slowly.",
      p6: "Notice what resonates.",
      p7: "Leave what doesn't.",
    },
    part1: {
      title: "Part 1 — Red Flags Are Patterns, Not Moments",
      p1: "A red flag is rarely one dramatic incident.",
      p2: "More often, it's a pattern:",
      list: [
        "How someone responds when things are uncomfortable",
        "What happens when expectations aren't met",
        "Whether accountability is present or consistently avoided",
      ],
      p3: "Everyone has off days.",
      p4: "Everyone makes mistakes.",
      p5: "What matters is what repeats.",
      reflection: "When I think back on past relationships, what patterns did I notice after I was already emotionally invested?",
    },
    part2: {
      title: "Part 2 — Inconsistency Is Information",
      p1: "Consistency isn't about perfection.",
      p2: "It's about predictability over time.",
      p3: "Pay attention to:",
      list1: [
        "Words that don't align with actions",
        "Affection that fluctuates dramatically without explanation",
        "Promises that feel sincere in the moment but fade later",
      ],
      p4: "Inconsistency often gets labeled as:",
      list2: [
        '"They\'re just busy"',
        '"They\'re independent"',
        '"They\'re figuring things out"',
      ],
      p5: "Sometimes that's true.",
      p6: "Sometimes it's not.",
      p7: "The key question isn't why — it's how it affects you.",
      reflection: "Do I feel grounded and secure in this connection, or am I often left guessing?",
    },
    part3: {
      title: "Part 3 — How Conflict Is Handled Matters More Than the Conflict Itself",
      p1: "Disagreement is inevitable.",
      p2: "Avoidance is optional.",
      p3: "Compatibility shows up in:",
      list1: [
        "Whether concerns can be raised without fear",
        "Whether defensiveness shuts down conversation",
        "Whether repair happens after tension",
      ],
      p4: "Pay attention to:",
      list2: [
        "Dismissiveness",
        "Stonewalling",
        "Turning every concern into your fault",
      ],
      p5: "Healthy conflict doesn't escalate endlessly — it moves toward understanding.",
      reflection: "When I express discomfort or concern, do I feel heard, minimized, or redirected?",
    },
    part4: {
      title: "Part 4 — Pace Is a Form of Compatibility",
      p1: "Fast doesn't always mean deep.",
      p2: "Slow doesn't always mean safe.",
      p3: "What matters is shared pace.",
      p4: "Notice:",
      list: [
        "Pressure to rush intimacy before clarity",
        "Resistance to defining intentions at all",
        "Mismatched expectations around time, communication, or commitment",
      ],
      p5: "Rushing can sometimes be a way to bypass important conversations.",
      p6: "Dragging things out indefinitely can be a way to avoid them.",
      reflection: "Does the pace of this connection feel mutual — or am I adjusting myself to keep it going?",
    },
    part5: {
      title: "Part 5 — Emotional Availability Is Shown, Not Claimed",
      p1: "Many people say they are emotionally available.",
      p2: "Look instead at:",
      list: [
        "Follow-through",
        "Willingness to engage in difficult conversations",
        "Capacity to sit with discomfort without shutting down",
      ],
      p3: "Emotional availability isn't about constant openness.",
      p4: "It's about presence when it matters.",
      reflection: "When things get real, does this person lean in, pull away, or deflect?",
    },
    part6: {
      title: "Part 6 — Compatibility Is About Everyday Life, Not Just Chemistry",
      p1: "Chemistry can start a connection.",
      p2: "Compatibility sustains one.",
      p3: "True alignment often shows up in:",
      list: [
        "How decisions are made",
        "Attitudes toward money, family, faith, and responsibility",
        "Expectations around partnership roles",
        "How life stress is handled",
      ],
      p4: "These aren't \"unromantic\" topics.",
      p5: "They're the foundation of long-term stability.",
      reflection: "Can I realistically imagine building a life with this person — not just spending time with them?",
    },
    part7: {
      title: "Part 7 — Pay Attention to How You Feel About Yourself",
      p1: "One of the clearest signals of alignment isn't about the other person at all.",
      p2: "It's about you.",
      p3: "Notice:",
      list: [
        "Do you feel calmer or more anxious?",
        "More secure or more uncertain?",
        "More yourself or more self-editing?",
      ],
      p4: "Healthy connections tend to expand you — not shrink you.",
      reflection: "Who am I becoming in this connection?",
    },
    closing: {
      title: "A Closing Thought",
      p1: "Clarity doesn't require certainty.",
      p2: "It requires honesty.",
      p3: "You don't need to label someone as \"wrong\" to recognize misalignment.",
      p4: "You don't need to justify walking away from something that doesn't feel right.",
      p5: "Compatibility isn't about finding perfection.",
      p6: "It's about finding fit.",
      p7: "And that's allowed.",
    },
  },

  // Summary Page
  summary: {
    title: "Your Relationship Map",
    congratulations: (name) => `Congratulations, ${name}. You've completed your journey of reflection. Here's a summary of your thoughts and values.`,
    summaryLabel: "Reflection Summary",
    summaryTitle: "To Be, Or Not To Be — The Relationship Map",
    sharingTitle: "Sharing Your Reflections",
    sharingDescription: "Your reflections are private by default. When you're ready, you may choose to share your completed Relationship Map with a potential partner as a tool for thoughtful conversation—not evaluation or matchmaking.",
    viewReflections: "View My Reflections",
    backToDashboard: "Back to Dashboard",
    privacyFooter: "Your reflections are private and secure. They are never shared without your explicit consent.",
    loadingSummary: "Loading your summary...",
    incompleteError: "Complete all checkpoints to view your summary",
    loadError: "Failed to load summary",
  },

  // Founding Member Offer
  foundingMember: {
    label: "Founding Member Invitation",
    title: "Join the First 50",
    description: "As one of our first members to complete the Relationship Map, you're invited to become a Founding Member with exclusive early access when we launch matching and messaging features.",
    priceLabel: (price) => `$${price}`,
    regularPrice: (price) => `$${price}/year`,
    benefitsText: "12 months of full access at launch",
    spotsRemaining: (spots) => `Only ${spots} spots remaining`,
    ctaButton: "Become a Founding Member",
    optionalNote: "This invitation is optional. Take your time.",
    closedMessage: "Founding Member invitations are now closed.",
    comingSoon: "Payment integration coming soon!",
  },

  // About Page
  about: {
    title: "About Our Love ♥ Connection",
    content: {
      intro: "Our Love Connection is for adults serious about building a real future — a guided system for adults who want clarity before commitment, not confusion after.",
      problem: "Most dating platforms prioritize speed and visibility — introducing people through photos, rapid swiping, and quick first impressions. While this format can create momentum, it often postpones the deeper conversations about values, lifestyle, family goals, finances, faith, emotional maturity, and commitment until attachment has already begun to form.",
      different: "Our Love Connection offers a different approach.",
      solution: "Instead of swiping or chasing endless conversations, this space guides you through intentional self-reflection around the foundations of a lasting partnership. You're encouraged to think carefully about what matters to you, what you're building toward, and whether a connection genuinely aligns with the future you want.",
      audience: "Our Love Connection is for adults who are dating with purpose — with the goal of building a marriage-ready partnership within the next few years. It is not designed for casual dating, swiping, or situationships.",
      values: "This is a calm, intentional space that supports thoughtful conversations, mutual understanding, and informed decisions — without pressure, games, or false urgency. There are no shortcuts here. Only clarity.",
      closing: "Whether you are dating seriously for the first time, returning after loss or divorce, or simply choosing to approach dating with intention, Our Love Connection exists to help you move forward with confidence and self-respect.",
      tagline: "Because building a real future matters.",
    },
    howItWorks: {
      title: "How It Works",
      intro: "Our Love ♥ Connection is for adults serious about building a real future — not just browsing profiles.",
      realPeople: "Yes, there are real people here.",
      profiles: "And yes, members do have profiles.",
      different: "We simply introduce them differently.",
      reflection: "Instead of leading with photos or endless scrolling, we begin with reflection. Everyone here completes To Be, Or Not To Be — The Relationship Map first — a guided process that helps clarify values, intentions, and long-term goals.",
      introduction: "Once that foundation is in place, members are introduced to potential partners in a more intentional way. Profiles and photos become visible when there is shared readiness and alignment — not before.",
      structure: "This structure is intentional. It was designed to protect clarity before attachment forms, and to ensure that conversations begin with alignment rather than impulse.",
      approach: "This approach keeps the focus on substance over speed, and helps connections form with clarity rather than pressure.",
      notForYou: "If you're looking for a place to browse or swipe, this may not be the right fit.",
      forYou: "If you're looking to connect thoughtfully with someone who is also dating with purpose, you're in the right place.",
    },
  },

  // Accessibility
  a11y: {
    togglePasswordVisibility: "Toggle password visibility",
    signOutButton: "Sign Out",
  },

  recovery: {
    title: "Account Recovery",
    subtitle: "Enter the email address associated with your account.",
    emailLabel: "Email Address",
    emailPlaceholder: "you@example.com",
    emailRequired: "Please enter your email address.",
    sendButton: "Send Recovery Link",
    sending: "Sending...",
    sentTitle: "Check Your Email",
    sentMessage: "If an account exists with this email, a recovery link has been sent. Please check your inbox and spam folder.",
    returnToLogin: "Return to Sign In",
    backToLogin: "Back to Sign In",
    securityNote: "For your security, we do not confirm whether an email is registered.",
    forgotLink: "Forgot password?",
  },

  resetPassword: {
    title: "Set New Password",
    subtitle: "Create a strong, unique password for your account.",
    newPasswordLabel: "New Password",
    newPasswordPlaceholder: "Enter new password",
    confirmLabel: "Confirm Password",
    confirmPlaceholder: "Confirm new password",
    submitButton: "Reset Password",
    resetting: "Resetting...",
    req8Chars: "At least 8 characters",
    reqUppercase: "One uppercase letter",
    reqLowercase: "One lowercase letter",
    reqNumber: "One number",
    reqSpecialChar: "One special character (e.g., !@#$%^&*)",
    weakPassword: "Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter, one number, and one special character.",
    mismatch: "Passwords do not match.",
    successTitle: "Password Reset",
    successMessage: "Your password has been updated. You can now sign in with your new password.",
    successToast: "Password reset successfully.",
    goToLogin: "Sign In",
    invalidTitle: "Invalid or Expired Link",
    invalidMessage: "This reset link is no longer valid. It may have expired or already been used.",
    tryAgain: "Request New Link",
    errorGeneric: "Something went wrong. Please try again.",
  },

  verification: {
    verifying: "Verifying your email...",
    successTitle: "Email Verified",
    successMessage: "Your email has been verified. You can now sign in to your account.",
    signIn: "Sign In",
    invalidTitle: "Invalid Link",
    invalidMessage: "This verification link is not valid. Please request a new one from the sign in page.",
    expiredTitle: "Link Expired",
    expiredMessage: "This verification link has expired. Please request a new one from the sign in page.",
    backToLogin: "Back to Sign In",
    pendingTitle: "Verify Your Email",
    pendingMessage: "We sent a verification link to:",
    checkSpam: "Please check your inbox and spam folder. The link expires in 24 hours.",
    goToLogin: "Go to Sign In",
    resendLink: "Resend verification email",
    resending: "Sending...",
    resendSuccess: "If an unverified account exists with this email, a verification link has been sent.",
    loginBlocked: "Please verify your email to continue.",
  },
};

/**
 * Helper function for accessing copy with a simple API
 * Usage: t('login.title') returns "Sign In"
 */
export const t = (key) => {
  const keys = key.split('.');
  let value = copy;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      console.warn(`Missing copy key: ${key}`);
      return key;
    }
  }
  
  return value;
};

export default copy;
